/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Accessible Language Switcher Component (Task 11.1.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useRef, useEffect } from 'react';
import { Globe, Check, ChevronDown } from 'lucide-react';
import { useI18n, SUPPORTED_LOCALES, LOCALE_CONFIG, type SupportedLocale } from '../../i18n';

export const LanguageSwitcher: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { currentLocale, setLocale, getMetadata } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const activeMeta = getMetadata();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative inline-block text-left ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-surface-border bg-surface text-foreground hover:bg-surface-elevated transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40"
        aria-haspopup="true"
        aria-expanded={isOpen}
      >
        <span className="text-sm leading-none">{activeMeta.flag}</span>
        <span className="hidden sm:inline">{activeMeta.nativeName}</span>
        <ChevronDown className="w-3.5 h-3.5 text-muted-fg" />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 rounded-xl bg-surface border border-surface-border shadow-xl py-1.5 z-50 animate-fade-in focus:outline-none">
          <div className="px-3 py-1.5 border-b border-surface-border text-[11px] font-semibold text-muted-fg uppercase tracking-wider flex items-center gap-1.5">
            <Globe className="w-3 h-3" />
            <span>Select Language</span>
          </div>

          <div className="py-1">
            {SUPPORTED_LOCALES.map((localeCode) => {
              const meta = LOCALE_CONFIG[localeCode as SupportedLocale];
              const isSelected = currentLocale === localeCode;

              return (
                <button
                  key={localeCode}
                  type="button"
                  onClick={() => {
                    setLocale(localeCode as SupportedLocale);
                    setIsOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs transition-colors ${
                    isSelected
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-foreground hover:bg-surface-elevated'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-base leading-none">{meta.flag}</span>
                    <div className="flex flex-col items-start leading-tight">
                      <span>{meta.nativeName}</span>
                      <span className="text-[10px] text-muted-fg">{meta.name}</span>
                    </div>
                  </div>
                  {isSelected && <Check className="w-3.5 h-3.5 text-primary" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
